import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import adminApi from '../../api/adminClient';
import './Admin.css';

const statusOptions = ['접수', '처리중', '완료'];
const sourceLabel = { quick: '메인 퀵문의', quote: '견적·상담신청' };

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('전체');
  const [openId, setOpenId] = useState(null);
  const [pendingStatuses, setPendingStatuses] = useState({});
  const [saving, setSaving] = useState({});
  const [deleteModal, setDeleteModal] = useState(null); // { item } | null
  const [deletePw, setDeletePw] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      setLoading(true);
      const data = await adminApi.get('/inquiries');
      setInquiries(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleStatusSelect(id, status) {
    setPendingStatuses(prev => ({ ...prev, [id]: status }));
  }

  async function handleStatusSave(id) {
    const status = pendingStatuses[id];
    if (status === undefined) return;
    setSaving(prev => ({ ...prev, [id]: true }));
    try {
      await adminApi.put(`/inquiries/${id}/status`, { status });
      setInquiries(prev => prev.map(i => i.id === id ? { ...i, status } : i));
      setPendingStatuses(prev => { const n = { ...prev }; delete n[id]; return n; });
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(prev => { const n = { ...prev }; delete n[id]; return n; });
    }
  }

  function openDeleteModal(item) {
    setDeleteModal({ item });
    setDeletePw('');
    setDeleteError('');
  }

  async function handleDeleteConfirm() {
    if (!deletePw) { setDeleteError('비밀번호를 입력해 주세요.'); return; }
    setDeleteLoading(true);
    setDeleteError('');
    try {
      await adminApi.post('/auth/verify-password', { password: deletePw });
    } catch (err) {
      setDeleteError(err.message || '비밀번호가 올바르지 않습니다.');
      setDeleteLoading(false);
      return;
    }
    try {
      await adminApi.delete(`/inquiries/${deleteModal.item.id}`);
      setInquiries(prev => prev.filter(i => i.id !== deleteModal.item.id));
      setDeleteModal(null);
    } catch (err) {
      setDeleteError(err.message || '삭제 중 오류가 발생했습니다.');
    } finally {
      setDeleteLoading(false);
    }
  }

  const filtered = inquiries.filter(i => filter === '전체' || i.status === filter);

  return (
    <AdminLayout>
      {/* 삭제 확인 모달 */}
      {deleteModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          zIndex: 20000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '2rem',
            width: '100%', maxWidth: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#dc2626' }}>문의 삭제</h3>
            </div>
            <p style={{ marginBottom: '1.25rem', fontSize: '0.95rem', color: '#374151' }}>
              삭제하시겠습니까? 관리자 비밀번호를 입력하세요.
            </p>
            <div className="form-group" style={{ marginBottom: '0.75rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: 600 }}>관리자 비밀번호</label>
              <input
                type="password"
                value={deletePw}
                onChange={e => { setDeletePw(e.target.value); setDeleteError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleDeleteConfirm()}
                placeholder="비밀번호 입력"
                autoFocus
                style={{ width: '100%', marginTop: '0.3rem' }}
              />
            </div>
            {deleteError && (
              <div className="form-error" style={{ marginBottom: '0.75rem' }}>{deleteError}</div>
            )}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-outline-navy" onClick={() => setDeleteModal(null)} disabled={deleteLoading}>취소</button>
              <button
                className="btn btn-primary"
                style={{ background: '#dc2626', borderColor: '#dc2626' }}
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
              >
                {deleteLoading ? '삭제 중...' : '삭제'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="admin-header">
        <div>
          <h1>문의·견적 접수 관리</h1>
          <p className="text-muted">메인 퀵문의 및 고객센터 견적·상담 신청 내역입니다. 총 {inquiries.length}건.</p>
        </div>
      </div>

      <div className="admin-filter-row">
        {['전체', ...statusOptions].map(f => (
          <button
            key={f}
            className={`type-filter__btn ${filter === f ? 'type-filter__btn--active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f}
          </button>
        ))}
      </div>

      {loading && <div className="state-message">불러오는 중입니다...</div>}
      {!loading && error && <div className="state-message state-message--error">{error}</div>}

      {!loading && !error && (
        filtered.length === 0 ? (
          <div className="state-message">접수된 문의가 없습니다.</div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>구분</th>
                  <th>회사명 / 이름</th>
                  <th>연락처</th>
                  <th>희망 서비스</th>
                  <th style={{ minWidth: 110 }}>등록일자</th>
                  <th>상태</th>
                  <th style={{ width: 170 }}>관리</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => (
                  <React.Fragment key={item.id}>
                    <tr
                      className="admin-table__row-clickable"
                      onClick={() => setOpenId(openId === item.id ? null : item.id)}
                    >
                      <td>
                        <span className="badge badge-blue">{sourceLabel[item.source] || item.source}</span>
                      </td>
                      <td>
                        <strong>{item.company}</strong>
                        {item.event_name && <span className="badge badge-accent" style={{ marginLeft: 6, fontSize: '0.75rem' }}>{item.event_name}</span>}
                        <div className="text-muted" style={{ fontSize: '0.82rem', marginTop: '0.2rem' }}>{item.name}</div>
                      </td>
                      <td>{item.phone}</td>
                      <td>{item.service_type || '-'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <div>{String(item.created_at).slice(0, 10)}</div>
                        <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: '0.15rem' }}>{String(item.created_at).slice(11, 16)}</div>
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <select
                          value={pendingStatuses[item.id] !== undefined ? pendingStatuses[item.id] : item.status}
                          onChange={e => handleStatusSelect(item.id, e.target.value)}
                          className={`admin-status-select admin-status-select--${item.status}`}
                        >
                          {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="admin-table__actions" onClick={e => e.stopPropagation()}>
                        <button
                          className="admin-action-btn"
                          onClick={() => handleStatusSave(item.id)}
                          disabled={saving[item.id] || pendingStatuses[item.id] === undefined}
                          style={{ opacity: pendingStatuses[item.id] !== undefined ? 1 : 0.45 }}
                        >
                          {saving[item.id] ? '저장중' : '저장'}
                        </button>
                        <button
                          className="admin-action-btn admin-action-btn--danger"
                          onClick={() => openDeleteModal(item)}
                        >
                          삭제
                        </button>
                      </td>
                    </tr>
                    {openId === item.id && (
                      <tr>
                        <td colSpan={7} className="admin-table__detail-row">
                          <div className="job-info-grid" style={{ marginBottom: '0.75rem' }}>
                            <div className="job-info-item"><span className="job-info-item__label">이메일</span><span className="job-info-item__value">{item.email || '-'}</span></div>
                            <div className="job-info-item"><span className="job-info-item__label">필요 인원</span><span className="job-info-item__value">{item.headcount || '-'}</span></div>
                          </div>
                          <strong style={{ fontSize: '0.85rem', color: 'var(--navy-800)' }}>문의 내용</strong>
                          <p style={{ marginTop: '0.4rem', marginBottom: 0, fontSize: '0.875rem', color: 'var(--gray-700)', lineHeight: 1.7 }}>
                            {item.message || '(작성된 문의 내용 없음)'}
                          </p>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </AdminLayout>
  );
}
